import type { PhysicalMeasurements } from "./annotations.js";

export type CanonicalBinding = "length" | "width" | "weight" | "count";
export type MeasurementAggregation = "mean" | "sum";
export type MeasurementUnit = "meters" | "grams" | "count";

export type MeasurementAxisEndpointKind = "start" | "end";

export interface ParsedMeasurementEndpointName {
  key: string;
  endpoint: MeasurementAxisEndpointKind;
}

const MEASUREMENT_AXIS_KEY_PATTERN = /^[A-Za-z0-9][A-Za-z0-9_]{0,79}$/;

export function isSafeMeasurementAxisKey(key: unknown): key is string {
  return typeof key === "string" && MEASUREMENT_AXIS_KEY_PATTERN.test(key);
}

export function parseMeasurementEndpointName(name: unknown): ParsedMeasurementEndpointName | null {
  if (typeof name !== "string") return null;

  switch (name) {
    case "head":
      return { key: "length", endpoint: "start" };
    case "tail":
      return { key: "length", endpoint: "end" };
    case "left":
      return { key: "width", endpoint: "start" };
    case "right":
      return { key: "width", endpoint: "end" };
  }

  const match = name.match(/^(.+)_(start|end)$/);
  if (!match || !isSafeMeasurementAxisKey(match[1])) return null;
  return { key: match[1], endpoint: match[2] as MeasurementAxisEndpointKind };
}

export function measurementFieldPathForAxisKey(key: string): string {
  if (key === "length") return "lengthM";
  if (key === "width") return "widthM";
  return `additionalMeasurements.${key}`;
}

export function getMeasurementAxisValue(
  measurements: PhysicalMeasurements | null | undefined,
  key: string
): number | undefined {
  if (key === "length") return measurements?.lengthM;
  if (key === "width") return measurements?.widthM;
  return measurements?.additionalMeasurements?.[key];
}

/** Return a measurement object with one axis value updated, preserving every
 * sibling canonical and additional measurement. */
export function withMeasurementAxisValue(
  measurements: PhysicalMeasurements,
  key: string,
  valueM: number
): PhysicalMeasurements {
  if (key === "length") return { ...measurements, lengthM: valueM };
  if (key === "width") return { ...measurements, widthM: valueM };
  return {
    ...measurements,
    additionalMeasurements: {
      ...(measurements.additionalMeasurements ?? {}),
      [key]: valueM,
    },
  };
}

/** Return a measurement object with one axis value removed, preserving every
 * sibling canonical and additional measurement. Prunes an emptied
 * additionalMeasurements container; returns undefined when nothing remains. */
export function withoutMeasurementAxisValue(
  measurements: PhysicalMeasurements | null | undefined,
  key: string
): PhysicalMeasurements | undefined {
  if (!measurements) return undefined;

  const next: PhysicalMeasurements = { ...measurements };
  if (key === "length") {
    delete next.lengthM;
  } else if (key === "width") {
    delete next.widthM;
  } else if (next.additionalMeasurements) {
    const additionalMeasurements = { ...next.additionalMeasurements };
    delete additionalMeasurements[key];
    if (Object.keys(additionalMeasurements).length > 0) next.additionalMeasurements = additionalMeasurements;
    else delete next.additionalMeasurements;
  }

  return Object.keys(next).length > 0 ? next : undefined;
}

export interface MeasurementDescriptor {
  key: string;
  label: string;
  binding: CanonicalBinding | null;
  unit: MeasurementUnit;
  aggregation: MeasurementAggregation;
  hasKeypoints: boolean;
  isRequired: boolean;
}

interface ImageMeasurementModelOptionsLike {
  kind?: string;
  hasWeightMeasurement?: boolean;
}

interface EntryConfigLike {
  type?: string;
  imageMeasurementConfig?: {
    imageMeasurementModelOptions?: ImageMeasurementModelOptionsLike;
  };
}

const DESCRIPTOR_DEFAULTS: Record<CanonicalBinding, MeasurementDescriptor> = {
  count: {
    key: "count",
    label: "Count",
    binding: "count",
    unit: "count",
    aggregation: "sum",
    hasKeypoints: false,
    isRequired: true,
  },
  length: {
    key: "length",
    label: "Length",
    binding: "length",
    unit: "meters",
    aggregation: "mean",
    hasKeypoints: true,
    isRequired: true,
  },
  width: {
    key: "width",
    label: "Width",
    binding: "width",
    unit: "meters",
    aggregation: "mean",
    hasKeypoints: true,
    isRequired: true,
  },
  weight: {
    key: "weight",
    label: "Weight",
    binding: "weight",
    unit: "grams",
    aggregation: "sum",
    hasKeypoints: false,
    isRequired: true,
  },
};

const CORE_LENGTH_DESCRIPTOR: MeasurementDescriptor = {
  key: "core_length",
  label: "Core Length",
  binding: null,
  unit: "meters",
  aggregation: "mean",
  hasKeypoints: true,
  isRequired: true,
};

const KEYPOINT_MODEL_KINDS = new Set(["spinachKeypoint", "arugulaKeypoint"]);

function descriptor(binding: CanonicalBinding, overrides?: Partial<MeasurementDescriptor>): MeasurementDescriptor {
  return { ...DESCRIPTOR_DEFAULTS[binding], ...overrides };
}

export function defaultLabel(binding: CanonicalBinding): string {
  switch (binding) {
    case "length":
      return "Length";
    case "width":
      return "Width";
    case "weight":
      return "Weight";
    case "count":
      return "Count";
  }
}

export function hasCustomLabel(descriptor: MeasurementDescriptor): boolean {
  return descriptor.binding === null || descriptor.label !== defaultLabel(descriptor.binding);
}

export function resolveMeasurementDescriptors(kind: string, hasWeightMeasurement: boolean): MeasurementDescriptor[] {
  if (KEYPOINT_MODEL_KINDS.has(kind)) {
    return [descriptor("count")];
  }

  if (kind === "bisectedLettuce") {
    const descriptors = [
      { ...CORE_LENGTH_DESCRIPTOR },
      descriptor("count"),
      descriptor("length", { label: "Head Length" }),
      descriptor("width", { label: "Head Width" }),
    ];
    if (hasWeightMeasurement) descriptors.push(descriptor("weight"));
    return descriptors;
  }

  const descriptors = [descriptor("count"), descriptor("length"), descriptor("width")];
  if (hasWeightMeasurement) descriptors.push(descriptor("weight"));
  return descriptors;
}

export function getDescriptorsForEntryConfig(entryConfig: EntryConfigLike | null | undefined): MeasurementDescriptor[] {
  if (entryConfig?.type !== "imageMeasurement") return [];

  const modelOptions = entryConfig.imageMeasurementConfig?.imageMeasurementModelOptions;
  return resolveMeasurementDescriptors(modelOptions?.kind ?? "none", modelOptions?.hasWeightMeasurement === true);
}

export function getUnboundDescriptors(entryConfig: EntryConfigLike | null | undefined): MeasurementDescriptor[] {
  return getDescriptorsForEntryConfig(entryConfig).filter((descriptor) => descriptor.binding === null);
}
